/**
 * Early preload for the vehicle detail album image.
 *
 * Why this exists
 * ---------------
 * The album's hero angle is the largest element in the first viewport on a
 * vehicle page, so it is the LCP element. But the album only renders once
 * /api/v1/car/<id> resolves, and on a throttled mobile connection that call
 * does not even START until ~5.7s because 317KB of third-party tag manager
 * occupies the network first. Measured result: LCP 2.4s -> 7.1s, purely from
 * the image being discovered late.
 *
 * The vehicle id is in the URL from the very first byte, and the id -> asset
 * mapping is static (fleetMedia.js). So the correct image can be preloaded as
 * soon as the main bundle executes, long before the API answers, which is what
 * this module does.
 *
 * This is the same trick the homepage hero already uses (scripts/inline-hero.js
 * injects a pre-paint hint), applied to the fleet — but deliberately kept in
 * the app bundle rather than in index.html, so none of the approved hero
 * pre-paint files are touched.
 */
import { heroAngle } from "../data/fleetMedia";

/** Pull the numeric vehicle id out of /<lang>/cars/<id> (any language prefix). */
const idFromPath = (pathname) => {
  const m = /\/(?:[a-z]{2}\/)?cars\/(\d+)/i.exec(pathname || "");
  return m ? Number(m[1]) : null;
};

/**
 * Pick the width the card/album will actually display, so the preload matches
 * the <picture> selection and the browser does not download twice.
 * The album renders at ~94vw on phones and ~62vw on desktop.
 */
const pickWidth = (vw) => {
  const target = vw < 768 ? vw * 0.94 : vw * 0.62;
  const dpr = window.devicePixelRatio || 1;
  const needed = target * dpr;
  if (needed <= 400) return 400;
  if (needed <= 800) return 800;
  return 1200;
};

/** Extract one width's URL out of a srcSet string. */
const fromSrcSet = (srcSet, width) => {
  if (!srcSet) return null;
  const want = `${width}w`;
  for (const part of srcSet.split(",")) {
    const [url, w] = part.trim().split(/\s+/);
    if (w === want) return url;
  }
  return null;
};

export const preloadVehicleHeroImage = () => {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    // Never spend a visitor's metered bytes on a speculative preload.
    const c = navigator.connection;
    if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ""))) return;

    const id = idFromPath(window.location.pathname);
    if (!id) return;

    const angle = heroAngle(id);
    if (!angle) return;

    const width = pickWidth(window.innerWidth || 390);
    // AVIF first, matching the <source> order the album declares.
    const href = fromSrcSet(angle.avif, width);
    if (!href) return;

    // Don't double-inject on client-side navigation back to the same car.
    if (document.head.querySelector(`link[rel="preload"][href="${href}"]`)) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = href;
    link.type = "image/avif";
    // fetchpriority high: this IS the LCP candidate, so it should outrank the
    // tag-manager scripts competing for the same connection.
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
  } catch (e) {
    /* A failed preload must never break the page — it is only a hint. */
  }
};

export default preloadVehicleHeroImage;
